import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import { aVista, pref } from '../utils/moneda';
import { saldoEnVista } from '../utils/indexacion';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// Pagos próximos de la empresa (#88), leídos de las facturas de compra
// abiertas (/compras/cxp). Arriba, por vencimiento (fecha + días de
// crédito): vencido / hoy / próximos 7 / próximos 30 días. Abajo, el
// próximo día con pagos programados en la Agenda (fecha_pago_prevista).
// Montos convertidos a la vista elegida con las tasas fijas de cada factura,
// igual que el tablero de Cuentas por Pagar.
export default function TarjetaPagosProximos({ empresaActiva }) {
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState(null);
  const [verEn, setVerEn] = useState('BCV');

  useEffect(() => {
    if (!empresaActiva) return;
    let vigente = true;
    api.get('/compras/cxp')
      .then(({ data }) => { if (vigente) setFacturas(data); })
      .catch(() => { if (vigente) setFacturas(null); });
    return () => { vigente = false; };
  }, [empresaActiva]);

  // Sin acceso a compras (rol o módulo): la tarjeta no se muestra.
  if (!facturas) return null;

  const monto = (f) => saldoEnVista(f, verEn, aVista);
  const grupo = (filtro) => {
    const fs = facturas.filter(filtro);
    return { cantidad: fs.length, total: fs.reduce((s, f) => s + monto(f), 0) };
  };
  const dias = (f) => Number(f.dias_vencido);
  const grupos = [
    { label: 'Vencido', clase: 'text-red-600', ...grupo((f) => dias(f) > 0) },
    { label: 'Hoy', clase: 'text-amber-600', ...grupo((f) => dias(f) === 0) },
    { label: 'Próx. 7 días', clase: 'text-slate-700', ...grupo((f) => dias(f) < 0 && dias(f) >= -7) },
    { label: 'Próx. 30 días', clase: 'text-slate-700', ...grupo((f) => dias(f) < -7 && dias(f) >= -30) }
  ];

  const hoy = hoyCaracas();
  const programadas = facturas.filter((f) => f.fecha_pago_prevista);
  const atrasadas = programadas.filter((f) => f.fecha_pago_prevista < hoy);
  const proximaFecha = programadas
    .map((f) => f.fecha_pago_prevista)
    .filter((d) => d >= hoy)
    .sort()[0];
  const proximo = proximaFecha ? grupo((f) => f.fecha_pago_prevista === proximaFecha) : null;

  return (
    <div
      {...filaClickeable(() => navigate('/reportes/cuentas-por-pagar'))}
      role="button"
      className={`bg-white rounded-xl shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow ${FOCO_FILA}`}
      title="Ir a Cuentas por Pagar"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm text-slate-500">Pagos próximos</p>
        <select
          value={verEn}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setVerEn(e.target.value)}
          className="border rounded-md px-1.5 py-0.5 text-xs text-slate-600"
          title="Moneda en que se muestran los montos"
        >
          <option value="BCV">BCV</option>
          <option value="USD">USD</option>
        </select>
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 items-baseline text-sm">
        {grupos.map((g) => (
          <React.Fragment key={g.label}>
            <span className="text-xs text-slate-500">{g.label}</span>
            <span className={`text-right tabular-nums font-semibold ${g.cantidad ? g.clase : 'text-slate-300'}`}>
              {pref(verEn)}{formatMonto(g.total)}
              <span className="text-xs font-normal text-slate-400"> ({g.cantidad})</span>
            </span>
          </React.Fragment>
        ))}
      </div>

      <p className="text-xs text-slate-500 mt-2 pt-2 border-t">
        {proximo
          ? <>Programado {proximaFecha === hoy ? 'hoy' : `el ${formatFecha(proximaFecha)}`}: <span className="font-medium text-slate-700 tabular-nums">{pref(verEn)}{formatMonto(proximo.total)}</span> ({proximo.cantidad})</>
          : 'Sin pagos programados en la agenda'}
      </p>
      {atrasadas.length > 0 && (
        <p className="text-xs text-amber-600">
          {atrasadas.length} programada{atrasadas.length === 1 ? '' : 's'} con fecha pasada sin pagar
        </p>
      )}
    </div>
  );
}
